<script>
	import { t } from '@resider/i18n';
	import Icon from '../Icon/Icon.svelte';

	/** @type {boolean} */
	export let disabled = false;
	/** @type {string} */
	export let id = '';
	/** @type {string} */
	export let label = '';
	/** @type {null | number} */
	export let maxlength = null;
	/** @type {null | number} */
	export let minlength = 8;
	/** @type {string | undefined} */
	export let name = undefined;
	/** @type {null | string} */
	export let pattern = null;
	/** @type {null | string} */
	export let placeholder = null;
	export let showPasswordMeter = false;
	/** @type {'sm' | 'md' | 'lg'} */
	export let size = 'md';
	/** @type {string | null} */
	export let value = null;

	$: passwordVisible = false;

	$: tag = $$slots.label || label ? 'label' : 'div';

	$: id = id ? `id-${crypto.randomUUID()}` : crypto.randomUUID();

	$: passwordStrength = calculateStrength(value);

	function calculateStrength(password) {
		if (password?.length < minlength) return 0;

		if (password?.length >= minlength && /\d/.test(password)) {
			if (/[a-z]/.test(password) && /[A-Z]/.test(password) && /\W/.test(password)) {
				return 3;
			}
			return 2;
		}
		return 1;
	}

	function togglePasswordVisibility() {
		passwordVisible = !passwordVisible;
	}
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<svelte:element
	this={tag}
	class="password-input {size} strength-{passwordStrength}"
	class:active={value?.length}
	class:disabled
	on:click
>
	{#if $$slots.label || label}
		<div class="label">
			<slot name="label">{label}</slot>
		</div>
	{/if}
	<div class="field">
		<slot name="left" />
		{#if passwordVisible}
			<input
				autocomplete="off"
				class:active={value?.length}
				{disabled}
				{id}
				{maxlength}
				{minlength}
				{name}
				{pattern}
				{placeholder}
				required
				type="text"
				bind:value
				on:blur
				on:focus
				on:input
			/>
		{:else}
			<input
				autocomplete="off"
				class:active={value?.length}
				{disabled}
				{id}
				{maxlength}
				{minlength}
				{name}
				{pattern}
				{placeholder}
				type="password"
				bind:value
				on:blur
				on:focus
				on:input
			/>
		{/if}

		<Icon
			name={passwordVisible ? 'eye-closed' : 'eye'}
			on:click={togglePasswordVisibility}
			--size="1.25rem"
		/>
	</div>

	{#if value?.length >= 1 && showPasswordMeter}
		<div class="password-meter">
			<div
				class="bar"
				class:active={passwordStrength === 0}
				style:--background={passwordStrength === 0
					? 'hsla(var(--hsl-error)'
					: 'hsla(var(--hsl-gray-light))'}
			>
				{$t('global.Weak')}
			</div>
			<div
				class="bar"
				class:active={passwordStrength === 1}
				style:--background={passwordStrength === 1
					? 'hsla(var(--hsl-warning)'
					: 'hsla(var(--hsl-gray-light))'}
			>
				{$t('global.Medium')}
			</div>
			<div
				class="bar"
				class:active={passwordStrength === 2}
				style:--background={passwordStrength === 2
					? 'hsla(var(--hsl-success)'
					: 'hsla(var(--hsl-gray-light))'}
			>
				{$t('global.Good')}
			</div>
			<div
				class="bar"
				class:active={passwordStrength === 3}
				style:--background={passwordStrength === 3
					? 'hsla(var(--hsl-primary)'
					: 'hsla(var(--hsl-gray-light))'}
			>
				{$t('global.Strong')}
			</div>
		</div>
	{/if}
</svelte:element>

<style>.password-input{--hsl-input-border:var(--hsl-gray-light);display:inline-grid;gap:.375rem}.password-input:not(.disabled):hover{--hsl-input-border:var(--hsl-primary), 0.5}.password-input:not(.disabled):focus-within{--hsl-input-border:var(--hsl-primary)}.password-input.active:not(.disabled){--hsl-input-border:var(--hsl-primary)}.password-input.disabled,.password-input.disabled input{cursor:not-allowed}.password-input :global(.icon){cursor:pointer;--size:var(--icon-size, 1.25rem)!important}.label,input{font:var(--f-ui-md-medium)}.label{color:hsla(var(--hsl-gray-extra-dark))}.field{align-items:center;display:inline-flex;gap:.25rem;background:hsla(var(--hsl-white));border:2px solid hsla(var(--hsl-input-border))}.sm .field{border-radius:.875rem;padding:0 .625rem;min-height:2.75rem}.md .field{padding:0 .625rem;min-height:3rem}.lg .field{padding:0 .75rem;min-height:4.25rem}.lg .field,.md .field{border-radius:1rem}input{-webkit-appearance:none;-moz-appearance:none;appearance:none;background:0 0;height:100%;outline:0;overflow:hidden;text-overflow:ellipsis;padding:0;width:100%}input:-moz-read-only:not([disabled]){color:hsla(var(--hsl-black),.75)!important}input:read-only:not([disabled]){color:hsla(var(--hsl-black),.75)!important}input::-moz-placeholder{overflow:hidden;text-overflow:ellipsis;white-space:none}input::placeholder{overflow:hidden;text-overflow:ellipsis;white-space:none}.password-meter{display:flex;grid-column:1/-1;gap:.5rem;padding-left:.25rem;padding-right:.25rem;transition:all var(--time-xs) ease-out;margin-top:.625rem;margin-bottom:.625rem}.password-meter .bar{display:flex;width:100%;justify-content:center;position:relative;padding-top:.625rem;font:var(--f-ui-sm-medium);color:hsla(var(--hsl-gray))}.password-meter .bar.active{color:hsla(var(--hsl-black))}.password-meter .bar::before{background:var(--background);position:absolute;left:0;right:0;top:0;content:"";display:flex;height:.375rem;width:100%;border-radius:.5rem}</style>
